<script setup lang="ts">
import type { PropType, Ref } from 'vue'
import type { MenuItem, MenuItemExtended, ServiceTitleLanguages, SideNavOptions } from '~/types/sideNav'
import { computed, onMounted } from 'vue-demi'
import CfIcon from '~/components/CfIcon.vue'

import { getServices } from '~/main'
import { ENVIRONMENTS, REGISTERED_SERVICES_INFO } from '~/modules/constants'

import { UserRole } from '~/types/sideNav'

const props = defineProps({
  environment: {
    type: String,
    required: true,
    validator(value: string) {
      return ENVIRONMENTS.includes(value)
    },
  },
  activeServiceUrl: {
    type: String,
    required: true,
  },
  menuOptions: {
    type: String,
    required: false,
  },
  userRole: {
    type: String,
    required: true,
  },
  externalNavigation: Boolean,
  language: {
    type: String as PropType<ServiceTitleLanguages>,
    default: 'en',
  },
})

const emit = defineEmits(['init', 'menu-item:click', 'logo:click'])

const {
  dashboard,
  job_platform,
  wizard,
  clarification_cases,
  my_orders,
  my_orders_labels,
  logistics,
  ai_config,
  business_config,
} = REGISTERED_SERVICES_INFO

const icons = import.meta.glob('../../../public/assets/icons/*.svg', {
  as: 'raw',
  eager: true,
})

const getIconSvgTemplate = (iconName: string) => icons[`../../../public/assets/icons/${iconName}.svg`]

const resolvedMenuOptions = computed<SideNavOptions | undefined>(() => {
  return props.menuOptions && JSON.parse(props.menuOptions as string)
})

function getNotificationCount(id: string) {
  return resolvedMenuOptions.value?.[id]?.notificationCount ?? 0
}

const logoClickHandler = props.externalNavigation
  ? (e: MouseEvent) => {
      e.preventDefault()

      const eventPayload = {
        id: dashboard.id,
        serviceUrl: getServices(props.environment)[dashboard.id],
      }

      // eslint-disable-next-line vue/custom-event-name-casing
      emit('logo:click', eventPayload)

      return false
    }
  : undefined

function clickHandler(serviceId: string) {
  return function (e: MouseEvent) {
    e.preventDefault()

    const eventPayload = {
      id: serviceId,
      serviceUrl: getServices(props.environment)[serviceId],
    }
    // eslint-disable-next-line vue/custom-event-name-casing
    emit('menu-item:click', eventPayload)

    return false
  }
}

function buildChildrenMenuItems(children: any[] | undefined): MenuItem[] | undefined {
  if (!children)
    return undefined

  return children.map(child => ({
    id: child.id,
    titleTranslations: child.titleTranslations,
    iconTemplate: getIconSvgTemplate(child.icon),
    serviceUrl: getServices(props.environment)[child.id],
    notificationCount: getNotificationCount(child.id),
    clickHandler: props.externalNavigation ? clickHandler(child.id) : undefined,
    permissions: [UserRole.CF_MANAGER], // Inherit parent permissions or define separately
    children: buildChildrenMenuItems(child.children),
  }))
}

const homeClickHandler = props.externalNavigation ? clickHandler(dashboard.id) : undefined

const menuItemsDef: Ref<MenuItem[]> = computed(() => {
  const items: MenuItem[] = [
    {
      id: wizard.id,
      titleTranslations: wizard.titleTranslations,
      iconTemplate: getIconSvgTemplate(wizard.icon),
      serviceUrl: getServices(props.environment)[wizard.id],
      notificationCount: getNotificationCount(wizard.id),
      clickHandler: props.externalNavigation ? clickHandler(wizard.id) : undefined,
      permissions: [UserRole.CF_MANAGER, UserRole.CF_REQUESTER],
    },
    {
      id: job_platform.id,
      titleTranslations: job_platform.titleTranslations,
      iconTemplate: getIconSvgTemplate(job_platform.icon),
      serviceUrl: getServices(props.environment)[job_platform.id],
      notificationCount: getNotificationCount(job_platform.id),
      clickHandler: props.externalNavigation ? clickHandler(job_platform.id) : undefined,
      permissions: [UserRole.CF_MANAGER, UserRole.CF_SERVICE_PROVIDER],
    },
    {
      id: clarification_cases.id,
      titleTranslations: clarification_cases.titleTranslations,
      iconTemplate: getIconSvgTemplate(clarification_cases.icon),
      serviceUrl: getServices(props.environment)[clarification_cases.id],
      notificationCount: getNotificationCount(clarification_cases.id),
      clickHandler: props.externalNavigation ? clickHandler(clarification_cases.id) : undefined,
      permissions: [UserRole.CF_MANAGER, UserRole.CF_SERVICE_PROVIDER, UserRole.CF_REQUESTER],
    },
    {
      id: my_orders.id,
      titleTranslations: my_orders.titleTranslations,
      iconTemplate: getIconSvgTemplate(my_orders.icon),
      serviceUrl: getServices(props.environment)[my_orders.id],
      notificationCount: getNotificationCount(my_orders.id),
      clickHandler: props.externalNavigation ? clickHandler(my_orders.id) : undefined,
      permissions: [UserRole.CF_MANAGER, UserRole.CF_REQUESTER],
    },
    {
      id: my_orders_labels.id,
      titleTranslations: my_orders_labels.titleTranslations,
      iconTemplate: getIconSvgTemplate(my_orders_labels.icon),
      serviceUrl: getServices(props.environment)[my_orders_labels.id],
      notificationCount: getNotificationCount(my_orders_labels.id),
      clickHandler: props.externalNavigation ? clickHandler(my_orders_labels.id) : undefined,
      permissions: [UserRole.CF_MANAGER, UserRole.CF_REQUESTER],
    },
    {
      id: logistics.id,
      titleTranslations: logistics.titleTranslations,
      iconTemplate: getIconSvgTemplate(logistics.icon),
      serviceUrl: getServices(props.environment)[logistics.id],
      notificationCount: getNotificationCount(logistics.id),
      clickHandler: props.externalNavigation ? clickHandler(logistics.id) : undefined,
      permissions: [UserRole.CF_SERVICE_PROVIDER, UserRole.CF_MANAGER],
    },
    {
      id: ai_config.id,
      titleTranslations: ai_config.titleTranslations,
      iconTemplate: getIconSvgTemplate(ai_config.icon),
      serviceUrl: getServices(props.environment)[ai_config.id],
      notificationCount: getNotificationCount(ai_config.id),
      clickHandler: props.externalNavigation ? clickHandler(ai_config.id) : undefined,
      permissions: [UserRole.CF_MANAGER],
      children: buildChildrenMenuItems(ai_config.children),
    },
    {
      id: business_config.id,
      titleTranslations: business_config.titleTranslations,
      iconTemplate: getIconSvgTemplate(business_config.icon),
      serviceUrl: getServices(props.environment)[business_config.id],
      notificationCount: getNotificationCount(business_config.id),
      clickHandler: props.externalNavigation ? clickHandler(business_config.id) : undefined,
      permissions: [UserRole.CF_MANAGER],
      children: buildChildrenMenuItems(business_config.children),
    },
  ]

  return items
})

const dashboardTitle = computed(() => dashboard.titleTranslations[props.language])

const menuItems = computed<MenuItemExtended[]>(() => {
  function processMenuItem(menuItemDef: MenuItem): MenuItemExtended {
    return {
      ...menuItemDef,
      title: menuItemDef.titleTranslations[props.language],
      isActive: () => menuItemDef.serviceUrl === props.activeServiceUrl,
      children: menuItemDef.children?.map(processMenuItem),
    }
  }

  return menuItemsDef.value
    .filter(({ permissions }: MenuItem) => permissions.map(String).includes(props.userRole))
    .map(processMenuItem)
})

function formatNotificationCount(count: number | undefined) {
  if (count !== undefined)
    return count > 999 ? '999+' : `${count}`

  return null
}

onMounted(() => {
  emit('init', menuItems.value)
})
</script>

<template>
  <aside class="cf-aside-menu">
    <div class="cf-p-8">
      <div class="cf-mb-48">
        <a :href="getServices(environment).dashboard" class="cf-aside-menu__branding" @click="logoClickHandler">
          <CfIcon
            id="cf_logo_otto" class="cf_logo pl_logo--red"
            :icon-svg="getIconSvgTemplate('otto-logo-01')"
          />
          <div class="cf-vertical-line" />
          <h4 class="cf-my-0">
            CONTENT FACTORY
          </h4>
        </a>
      </div>

      <nav class="menu">
        <a :href="getServices(environment).dashboard" @click="homeClickHandler">
          <header
            class="cf-menu__header cf_headline50"
            :class="{ 'cf-list-item--active': activeServiceUrl === getServices(environment).dashboard }"
          >
            <CfIcon :icon-svg="getIconSvgTemplate('home')" class="cf-menu__header_icon" />
            <span class="cf-pl-8">{{ dashboardTitle }}</span>
          </header>
        </a>

        <div class="cf-menu__items">
          <div class="cf-menu__items-title">
            Content factory Services
          </div>
          <ul class="cf-list">
            <template v-for="menuItem in menuItems" :key="menuItem.id">
              <li>
                <a
                  :data-service-id="menuItem.id"
                  :href="menuItem.serviceUrl"
                  class="cf-list-item-link"
                  @click="menuItem.clickHandler"
                >
                  <div class="cf-list-item" :class="{ 'cf-list-item--active': menuItem.isActive() }">
                    <CfIcon :icon-svg="menuItem.iconTemplate" class="cf-list-item__icon" />
                    <span class="cf-list-item__text">{{ menuItem.title }}</span>
                    <div v-if="menuItem.notificationCount" class="cf-notification-badge">
                      {{ formatNotificationCount(menuItem.notificationCount) }}
                    </div>
                  </div>
                </a>

                <!-- Nested children -->
                <ul v-if="menuItem.children && menuItem.children.length > 0" class="cf-list cf-list--nested">
                  <li
                    v-for="childItem in menuItem.children"
                    :key="childItem.id"
                  >
                    <a
                      :data-service-id="childItem.id"
                      :href="childItem.serviceUrl"
                      class="cf-list-item-link"
                      @click="childItem.clickHandler"
                    >
                      <div class="cf-list-item cf-list-item--child" :class="{ 'cf-list-item--active': childItem.isActive() }">
                        <CfIcon :icon-svg="childItem.iconTemplate" class="cf-list-item__icon--small" />
                        <span class="cf-list-item__text">{{ childItem.title }}</span>
                        <div v-if="childItem.notificationCount" class="cf-notification-badge">
                          {{ formatNotificationCount(childItem.notificationCount) }}
                        </div>
                      </div>
                    </a>
                  </li>
                </ul>
              </li>
            </template>
          </ul>
        </div>
      </nav>
    </div>
  </aside>
</template>

<style scoped>
.cf-aside-menu {
  background-color: white;
  height: 100%;
  max-height: 100vh;
  width: 280px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

a {
  text-decoration: none;
  color: black;
}

.cf-aside-menu__branding {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  -webkit-align-items: center;
  align-items: center;
}

.cf_logo {
  max-width: 77px;
  max-height: 26px;
  display: block;
  fill: currentColor;
  color: rgb(240, 0, 32);
}

.cf-vertical-line {
  margin-left: 7px;
  border-left: 2px solid;
  border-color: #777777;
  height: 26px;
  left: 50%;
}

.cf-aside-menu__branding h4 {
  font-weight: bold;
  color: #777777;
  text-transform: uppercase;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.26em;
  width: 78px;
  height: 32px;
  padding-left: 12px;
}

.cf-menu__header {
  border-radius: 0.25rem;
  margin-bottom: 24px;
  padding: 10px 8px;
}

.cf-menu__items-title {
  font-size: 12px;
  line-height: 16px;
  color: #777777;
  text-transform: uppercase;
  padding-left: 8px;
  padding-right: 8px;
}

.cf-list {
  margin: 8px 0;
  padding-left: 0;
  padding-right: 0;
  list-style-type: none;
}

.cf-list > li {
  list-style: none;
}

.cf-list-item-link {
  cursor: pointer;
  display: block;
}

.cf-menu__header:hover,
.cf-menu__header:focus-visible,
.cf-list-item-link:hover .cf-list-item,
.cf-list-item-link:focus-visible .cf-list-item {
  border-radius: 0.25rem;
  background-color: #E6F4FF;
}

.cf-list a {
  cursor: pointer;
}

.cf-list--nested {
  margin-top: 0;
  margin-bottom: 4px;
  padding-left: 0;
}

.cf-list-item {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  -webkit-justify-content: space-between;
  justify-content: flex-start;
  align-items: center;
  margin-top: 0;
  margin-bottom: 4px;
  padding: 10px 8px;
  color: #222222;
  position: relative;
}

.cf-list-item--child {
  margin-left: 40px;
  margin-bottom: 2px;
  padding: 5px 8px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: #777777;
}

.cf-list-item__icon--small {
  width: 16px;
  height: 16px;
}

.cf-list-item--child .cf-list-item__text {
  padding-left: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
}

.cf-list-item--active {
  border-radius: 0.25rem;
  background-color: #f0f0f0;
  font-weight: 700;
}

.cf-list-item--child.cf-list-item--active .cf-list-item__text {
  color: #222222;
}

.cf-notification-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 7px;
  min-height: 16px;
  margin-left: auto;
  border-radius: 10px;
  padding: 0.5px 6px 0.5px 6px;
  background-color: #f00020;
  font-size: 12px;
  font-weight: bold;
  color: #fff;
  line-height: 16px;
  height: fit-content;
}

.cf-menu__header svg {
  margin-right: 8px;
}

.cf-menu__header_icon {
  display: inline-block;
  vertical-align: bottom;
}

.cf-menu__header_icon,
.cf-list-item__icon {
  width: 24px;
  height: 24px;
}

.cf-list-item__text {
  padding-left: 8px;
  font-size: 14px;
  line-height: 24px;
  font-weight: 500;
}

.cf-list-item--active .cf-list-item__text {
  font-weight: 700;
}

.cf-my-0 {
  margin-top: 0;
  margin-bottom: 0;
}

.cf-pl-8 {
  padding-left: 8px;
}
.cf-p-8 {
  padding: 2rem;
}

.cf-mb-48 {
  margin-bottom: 48px;
}

.cf_headline50 {
  font-size: 14px;
  line-height: 24px;
}
</style>

<style>
/*
 * Unscoped block: v-html-injected SVGs (CfIcon) do not receive the scoped
 * attribute, so scoped selectors cannot target them. This block is safe here
 * because SideNav is a Shadow DOM custom element — all styles are
 * naturally confined to the shadow root.
 *
 * Forces every icon SVG to fill its container (.cf-list-item__icon = 24 px,
 * .cf-list-item__icon--small = 16 px) regardless of the SVG's own
 * width/height attributes, using the viewBox for correct scaling.
 */
.cf-menu__header_icon svg,
.cf-list-item__icon svg,
.cf-list-item__icon--small svg {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
